import React, {Suspense, lazy, useCallback, useEffect, useRef, useState} from 'react'

import {BranchInfoBar, BranchInfoBarErrorBanner} from '@github-ui/code-view-shared/components/directory/BranchInfoBar'
import {AddFileDropdownButton} from '@github-ui/code-view-shared/components/AddFileDropdownButton'
import {CodeDropdownButton} from '@github-ui/code-dropdown-button'
import type {CodespacesTabProps} from '@github-ui/code-dropdown-button/components/CodespacesTab'
import {CodespacesTab} from '@github-ui/code-dropdown-button/components/CodespacesTab'
import {buildCodespacesPath} from '@github-ui/code-dropdown-button/build-codespaces-path'
import {LocalTab} from '@github-ui/code-dropdown-button/components/LocalTab'
import {AllShortcutsEnabled} from '@github-ui/code-view-shared/components/AllShortcutsEnabled'
import {useBranchAndTagCount, type BranchAndTagCount} from '@github-ui/code-view-shared/contexts/BranchAndTagContext'
import {CodeViewError} from '@github-ui/code-view-shared/components/CodeViewError'
import {ReposHeaderRefSelector} from '@github-ui/code-view-shared/components/ReposHeaderRefSelector'
import {useReposAppPayload} from '@github-ui/code-view-shared/contexts/FilesPageInfoContext'
import {usePath} from '@github-ui/code-view-shared/contexts/PathContext'
import {useRefInfo} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import type {DataRouterFileOverviewPayload, FileOverviewPayload} from '@github-ui/code-view-types'
import {useCurrentRepository} from '@github-ui/current-repository'
import {useCurrentUser} from '@github-ui/current-user'
import {branchesPath, repositoryTreePath, tagsPath, branchAndTagCountPath} from '@github-ui/paths'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Link} from '@github-ui/react-core/link'
// eslint-disable-next-line no-restricted-imports
import {ScreenSize} from '@github-ui/screen-size'
import {useNavigate} from '@github-ui/use-navigate'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {GitBranchIcon, KebabHorizontalIcon, PlusIcon, SearchIcon, TagIcon, UploadIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, IconButton} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {DirectoryContent} from '@github-ui/code-view-shared/components/directory/DirectoryContent'

import {OverviewFiles} from './OverviewRepoFiles'
import {OverviewHeader} from './header/OverviewHeader'
import BranchRenamePopover from './popovers/BranchRenamePopover'
import ParentBranchRenamePopover from './popovers/ParentBranchRenamePopover'
import {useShortcut} from '@github-ui/code-view-shared/hooks/shortcuts'
import {NewAgentTaskButton} from '@github-ui/new-agent-task-button/NewAgentTaskButton'
import styles from './OverviewContent.module.css'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {clsx} from 'clsx'
import {useIsDataRouterEnabled} from '@github-ui/react-core/future/use-is-data-router-enabled'

const FileResultsList = lazy(() => import('@github-ui/code-view-shared/components/files-search/FileResultsList'))

export default function OverviewContent({
  payload,
  className,
}: {
  payload: FileOverviewPayload | DataRouterFileOverviewPayload
  className?: string
}) {
  const isDataRouter = useIsDataRouterEnabled()

  return isDataRouter ? (
    <OverviewContentWithContext payload={payload} className={className} />
  ) : (
    <OverviewContentWithFetch payload={payload} className={className} />
  )
}

function OverviewContentWithContext({
  payload,
  className,
}: {
  payload: FileOverviewPayload | DataRouterFileOverviewPayload
  className?: string
}) {
  const branchAndTagCount = useBranchAndTagCount()

  return <OverviewContentInternal branchAndTagCount={branchAndTagCount} payload={payload} className={className} />
}

function OverviewContentWithFetch({
  payload,
  className,
}: {
  payload: FileOverviewPayload | DataRouterFileOverviewPayload
  className?: string
}) {
  const [branchAndTagCount, setBranchAndTagCount] = useState<BranchAndTagCount>(null)
  const overview = payload.overview
  const repo = useCurrentRepository()

  useEffect(() => {
    const fetchBranchAndTagCount = async () => {
      const response = await verifiedFetchJSON(branchAndTagCountPath(repo), {method: 'get'})
      if (response.ok) {
        const data = await response.json()
        setBranchAndTagCount(data)
      }
    }
    if (overview) {
      fetchBranchAndTagCount()
    }
  }, [overview, repo])

  return <OverviewContentInternal branchAndTagCount={branchAndTagCount} payload={payload} className={className} />
}

function OverviewContentInternal({
  payload,
  branchAndTagCount,
  className,
}: {
  payload: FileOverviewPayload | DataRouterFileOverviewPayload
  branchAndTagCount: BranchAndTagCount
  className?: string
}) {
  const {helpUrl} = useReposAppPayload()

  const repo = useCurrentRepository()
  const currentUser = useCurrentUser()
  const path = usePath()
  const refInfo = useRefInfo()
  const {findFileWorkerPath} = useReposAppPayload()
  const navigate = useNavigate()
  const {findFilesShortcut} = useShortcut()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const decoupledModeEnabled = isFeatureEnabled('copilot_mission_control_decoupled_mode')

  const overview = payload.overview
  const codeButton = overview.codeButton
  const local = {...codeButton.local, helpUrl}
  const codespaces = {
    hasAccessToCodespaces: codeButton.hasAccessToCodespaces,
    repoPolicyInfo: codeButton.repoPolicyInfo,
    contactPath: codeButton.contactPath,
    currentUserIsEnterpriseManaged: codeButton.currentUserIsEnterpriseManaged,
    enterpriseManagedBusinessName: codeButton.enterpriseManagedBusinessName,
    newCodespacePath: codeButton.newCodespacePath,
    codespacesPath: buildCodespacesPath(repo.id, refInfo.name),
    isLoggedIn: !!currentUser,
  } as CodespacesTabProps

  const isCopilotSWEAgentEnabled = overview.copilotSWEAgentEnabled ?? false
  const isDataRouter = useIsDataRouterEnabled()

  const onFindFilesShortcut = React.useCallback(() => {
    if (window.innerWidth < ScreenSize.xlarge) {
      navigate(
        isDataRouter
          ? `/${repo.ownerLogin}/${repo.name}/tree/${refInfo.name}?search=1`
          : `${window.location.pathname}?search=1`,
      )
    } else {
      inputRef.current?.focus()
    }
  }, [navigate, isDataRouter, repo.name, repo.ownerLogin, refInfo.name])

  const navigateToSearch = useCallback(() => {
    navigate(
      isDataRouter
        ? `/${repo.ownerLogin}/${repo.name}/tree/${refInfo.name}?search=1`
        : `${window.location.pathname}?search=1`,
    )
  }, [navigate, isDataRouter, repo.name, repo.ownerLogin, refInfo.name])

  return (
    <>
      {payload.error ? (
        <CodeViewError {...payload.error} />
      ) : (
        <div className={clsx(styles.Box, className)}>
          <OverviewHeader payload={payload} />
          <div className={styles.Box_1}>
            <div className={styles.Box_2}>
              <div className={styles.Box_3}>
                <ReposHeaderRefSelector buttonClassName="overview-ref-selector width-full" allowResizing />
                {overview.popovers.rename ? (
                  <BranchRenamePopover rename={overview.popovers.rename} />
                ) : overview.popovers.renamedParentRepo ? (
                  <ParentBranchRenamePopover
                    branchName={overview.popovers.renamedParentRepo.branchName}
                    nameWithOwner={overview.popovers.renamedParentRepo.nameWithOwner}
                  />
                ) : null}
              </div>

              <div className={styles.Box_4}>
                <Button
                  as="a"
                  leadingVisual={GitBranchIcon}
                  variant="invisible"
                  href={branchesPath({repo})}
                  className={styles.Button}
                >
                  {branchAndTagCount ? (
                    <span>
                      <strong className="color-fg-default">{branchAndTagCount.branches} </strong>
                      {branchAndTagCount.branches === 1 ? 'Branch' : 'Branches'}
                    </span>
                  ) : (
                    'Branches'
                  )}
                </Button>

                <Button
                  as="a"
                  leadingVisual={TagIcon}
                  variant="invisible"
                  href={tagsPath({repo})}
                  className={styles.Button}
                >
                  {branchAndTagCount ? (
                    <span>
                      <strong className="color-fg-default">{branchAndTagCount.tags} </strong>
                      {branchAndTagCount.tags === 1 ? 'Tag' : 'Tags'}
                    </span>
                  ) : (
                    'Tags'
                  )}
                </Button>
              </div>

              <div className={styles.Box_5}>
                <Button
                  as="a"
                  aria-label="Go to Branches page"
                  icon={GitBranchIcon}
                  variant="invisible"
                  href={branchesPath({repo})}
                  className={styles.Button_1}
                />
                <Button
                  as="a"
                  aria-label="Go to Tags page"
                  icon={TagIcon}
                  variant="invisible"
                  href={tagsPath({repo})}
                  className={styles.Button_1}
                />
              </div>
            </div>
            <div className={styles.Box_6}>
              <div className={styles.Box_7}>
                <AllShortcutsEnabled>
                  <button hidden data-hotkey={findFilesShortcut.hotkey} onClick={onFindFilesShortcut} />
                </AllShortcutsEnabled>
                <div className={styles.Box_8}>
                  <Suspense fallback={null}>
                    <FileResultsList
                      commitOid={refInfo.currentOid}
                      findFileWorkerPath={findFileWorkerPath}
                      searchBoxRef={inputRef}
                      className={styles.FileResultsList}
                    />
                  </Suspense>
                </div>
                <div className={styles.Box_9}>
                  <Button onClick={navigateToSearch}>Go to file</Button>
                </div>
                <AddFileDropdownButton useDynamicSizing />
              </div>
              {isCopilotSWEAgentEnabled && (
                <NewAgentTaskButton
                  isIconButton
                  currentRepo={repo}
                  hideRepoSelector
                  eventType="new_agent_task_button_repo_overview"
                  decoupledModeEnabled={decoupledModeEnabled}
                />
              )}
              <CodeDropdownButton
                refName={refInfo.name}
                primary={!overview.templateButton}
                showCodespacesTab={codeButton.codespacesEnabled}
                isEnterprise={codeButton.isEnterprise}
                localTab={<LocalTab {...local} />}
                codespacesTab={<CodespacesTab {...codespaces} />}
              />
              <div className={styles.Box_10}>
                <ActionMenu>
                  <ActionMenu.Anchor>
                    <IconButton tooltipDirection="n" icon={KebabHorizontalIcon} aria-label="Open more actions menu" />
                  </ActionMenu.Anchor>

                  <ActionMenu.Overlay>
                    <ActionList>
                      <ActionList.LinkItem href={branchesPath({repo})}>
                        <ActionList.LeadingVisual>
                          <Octicon icon={GitBranchIcon} />
                        </ActionList.LeadingVisual>
                        Branches
                      </ActionList.LinkItem>
                      <ActionList.LinkItem href={tagsPath({repo})}>
                        <ActionList.LeadingVisual>
                          <Octicon icon={TagIcon} />
                        </ActionList.LeadingVisual>
                        Tags
                      </ActionList.LinkItem>
                      <ActionList.Divider />
                      <ActionList.Item onSelect={navigateToSearch}>
                        <ActionList.LeadingVisual>
                          <Octicon icon={SearchIcon} />
                        </ActionList.LeadingVisual>
                        Go to file
                      </ActionList.Item>
                      <ActionList.LinkItem
                        as={Link}
                        to={repositoryTreePath({repo, path, commitish: refInfo.name, action: 'new'})}
                      >
                        <ActionList.LeadingVisual>
                          <Octicon icon={PlusIcon} />
                        </ActionList.LeadingVisual>
                        Create new file
                      </ActionList.LinkItem>
                      <ActionList.LinkItem
                        href={repositoryTreePath({repo, path, commitish: refInfo.name, action: 'upload'})}
                      >
                        <ActionList.LeadingVisual>
                          <Octicon icon={UploadIcon} />
                        </ActionList.LeadingVisual>
                        Upload file
                      </ActionList.LinkItem>
                    </ActionList>
                  </ActionMenu.Overlay>
                </ActionMenu>
              </div>
            </div>
          </div>
          {payload.tree.showBranchInfobar && (
            <ErrorBoundary fallback={<BranchInfoBarErrorBanner />}>
              <BranchInfoBar />
            </ErrorBoundary>
          )}
          <div className={styles.Box_11}>
            <DirectoryContent overview={payload.overview} />
            <OverviewFiles
              initialFiles={overview.overviewFiles}
              shouldRecommendReadme={overview.banners.shouldRecommendReadme}
              isPersonalRepo={overview.banners.isPersonalRepo}
            />
          </div>
        </div>
      )}
    </>
  )
}
